import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    FormControl,
    FormControlLabel,
    Switch,
    Select,
    MenuItem,
    InputLabel,
    TextField,
    Chip,
    Box,
    Typography,
    Alert,
    FormGroup,
    Checkbox,
    Autocomplete,
    Stack,
    IconButton,
    Tooltip
} from '@mui/material';
import {
    Security as SecurityIcon,
    Info as InfoIcon,
    Warning as WarningIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import authService from '../services/auth.service';

interface SensitiveDataSettingsProps {
    open: boolean;
    onClose: () => void;
    document: any;
    onUpdate: () => void;
}

interface User {
    _id: string;
    fullName: string;
    email: string;
}

const SensitiveDataSettings: React.FC<SensitiveDataSettingsProps> = ({
    open,
    onClose,
    document,
    onUpdate
}) => {
    const [isSensitive, setIsSensitive] = useState(false);
    const [sensitiveDataCategory, setSensitiveDataCategory] = useState('confidential');
    const [allowedRoles, setAllowedRoles] = useState<string[]>([]);
    const [allowedUsers, setAllowedUsers] = useState<string[]>([]);
    const [sensitiveDataReason, setSensitiveDataReason] = useState('');
    const [loading, setLoading] = useState(false);
    const [users, setUsers] = useState<User[]>([]);
    const [error, setError] = useState('');

    const currentUser = authService.getUser();
    const userRole = currentUser?.role;
    const canEditSensitive = userRole === 'admin' || userRole === 'manager';

    useEffect(() => {
        if (document && open) {
            setIsSensitive(document.isSensitive || false);
            setSensitiveDataCategory(document.sensitiveDataCategory || 'confidential');
            setAllowedRoles(document.allowedRoles || []);
            setAllowedUsers(document.allowedUsers || []);
            setSensitiveDataReason(document.sensitiveDataReason || '');
            fetchUsers();
        }
    }, [document, open]);

    const fetchUsers = async () => {
        try {
            const api = authService.getApiInstance();
            const response = await api.get('/users');
            setUsers(response.data.users || []);
        } catch (error) {
            console.error('Failed to fetch users:', error);
        }
    };

    const handleSubmit = async () => {
        if (!canEditSensitive) {
            setError('You do not have permission to update sensitive settings');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const api = authService.getApiInstance();
            await api.patch(`/documents/${document._id}/sensitive`, {
                isSensitive,
                sensitiveDataCategory: isSensitive ? sensitiveDataCategory : undefined,
                allowedRoles: isSensitive ? allowedRoles : undefined,
                allowedUsers: isSensitive ? allowedUsers : undefined,
                sensitiveDataReason: isSensitive ? sensitiveDataReason : undefined
            });

            onUpdate();
            onClose();
        } catch (error: any) {
            setError(error.response?.data?.error || 'Failed to update sensitive settings');
        } finally {
            setLoading(false);
        }
    };

    const categoryDescriptions = {
        'confidential': 'Internal use only, standard security',
        'highly-confidential': 'Restricted access, enhanced security',
        'restricted': 'Need-to-know basis only',
        'internal-only': 'Not for external sharing'
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SecurityIcon color="primary" />
                        <Typography variant="h6">Sensitive Data Settings</Typography>
                    </Box>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                {!canEditSensitive && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        Only administrators and managers can modify sensitive data settings
                    </Alert>
                )}

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <Stack spacing={3}>
                    {/* Main toggle */}
                    <FormControlLabel
                        control={
                            <Switch
                                checked={isSensitive}
                                onChange={(e) => setIsSensitive(e.target.checked)}
                                disabled={!canEditSensitive}
                            />
                        }
                        label={
                            <Box>
                                <Typography variant="body1">Mark as Sensitive Document</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Restrict access to authorized users only
                                </Typography>
                            </Box>
                        }
                    />

                    {isSensitive && (
                        <>
                            {/* Category selection */}
                            <FormControl fullWidth>
                                <InputLabel>Sensitivity Level</InputLabel>
                                <Select
                                    value={sensitiveDataCategory}
                                    onChange={(e) => setSensitiveDataCategory(e.target.value)}
                                    label="Sensitivity Level"
                                    disabled={!canEditSensitive}
                                >
                                    <MenuItem value="confidential">
                                        <Stack>
                                            <Typography>Confidential</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {categoryDescriptions.confidential}
                                            </Typography>
                                        </Stack>
                                    </MenuItem>
                                    <MenuItem value="highly-confidential">
                                        <Stack>
                                            <Typography>Highly Confidential</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {categoryDescriptions['highly-confidential']}
                                            </Typography>
                                        </Stack>
                                    </MenuItem>
                                    <MenuItem value="restricted">
                                        <Stack>
                                            <Typography>Restricted</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {categoryDescriptions.restricted}
                                            </Typography>
                                        </Stack>
                                    </MenuItem>
                                    <MenuItem value="internal-only">
                                        <Stack>
                                            <Typography>Internal Only</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {categoryDescriptions['internal-only']}
                                            </Typography>
                                        </Stack>
                                    </MenuItem>
                                </Select>
                            </FormControl>

                            {/* Allowed roles */}
                            <Box>
                                <Typography variant="subtitle2" gutterBottom>
                                    Allowed Roles
                                </Typography>
                                <FormGroup row>
                                    {['admin', 'manager', 'user', 'viewer'].map((role) => (
                                        <FormControlLabel
                                            key={role}
                                            control={
                                                <Checkbox
                                                    checked={allowedRoles.includes(role)}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setAllowedRoles([...allowedRoles, role]);
                                                        } else {
                                                            setAllowedRoles(allowedRoles.filter(r => r !== role));
                                                        }
                                                    }}
                                                    disabled={!canEditSensitive || role === 'admin'}
                                                />
                                            }
                                            label={role.charAt(0).toUpperCase() + role.slice(1)}
                                        />
                                    ))}
                                </FormGroup>
                                {allowedRoles.includes('admin') && (
                                    <Typography variant="caption" color="text.secondary">
                                        Administrators always have access to all documents
                                    </Typography>
                                )}
                            </Box>

                            {/* Allowed users */}
                            <Autocomplete
                                multiple
                                options={users}
                                getOptionLabel={(option) => `${option.fullName} (${option.email})`}
                                value={users.filter(u => allowedUsers.includes(u._id))}
                                onChange={(_, newValue) => {
                                    setAllowedUsers(newValue.map(u => u._id));
                                }}
                                disabled={!canEditSensitive}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Specific Users"
                                        placeholder="Select users..."
                                        helperText="Grant access to specific users regardless of their role"
                                    />
                                )}
                                renderTags={(value, getTagProps) =>
                                    value.map((option, index) => (
                                        <Chip
                                            variant="outlined"
                                            label={option.fullName}
                                            {...getTagProps({ index })}
                                        />
                                    ))
                                }
                            />

                            {/* Reason */}
                            <TextField
                                fullWidth
                                label="Reason for Sensitivity"
                                multiline
                                rows={3}
                                value={sensitiveDataReason}
                                onChange={(e) => setSensitiveDataReason(e.target.value)}
                                disabled={!canEditSensitive}
                                helperText="Explain why this document is marked as sensitive"
                            />


                            {/* Warning */}
                            <Alert severity="info" icon={<InfoIcon />}>
                                <Typography variant="body2">
                                    Marking a document as sensitive will:
                                </Typography>
                                <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                                    <li>Restrict access to authorized users only</li>
                                    <li>Log all access attempts</li>
                                    <li>Show a security indicator on the document</li>
                                    <li>Exclude it from public searches</li>
                                </ul>
                            </Alert>
                        </>
                    )}
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={loading || !canEditSensitive}
                    startIcon={<SecurityIcon />}
                >
                    {loading ? 'Saving...' : 'Save Settings'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SensitiveDataSettings;